import type {ReactNode} from "react";
import {createContext, useCallback, useContext, useEffect, useMemo, useRef} from "react";
import {isEventWithinInteractiveNode} from "./dom-helpers";
import {useGlobalKeyPress} from "./KeyPress";

interface HandlerObj {
  handler: (e: React.MouseEvent | KeyboardEvent, force?: boolean) => boolean | void;
  strict?: boolean;
  escOnly?: boolean;
}

interface ClickOutsideContextValue {
  register: (handlerObj: HandlerObj) => () => void;
}

const ClickOutsideContext = createContext<ClickOutsideContextValue>({
  register: () => {
    if (process.env.NODE_ENV !== "production") {
      console.warn("Please use ClickOutsideProvider!");
    }
    return () => {};
  },
});

interface ClickOutsideProviderProps {
  children: (props: {
    onClick: (e: React.MouseEvent) => void;
    onMouseDownCapture: (e: React.MouseEvent) => void;
  }) => ReactNode;
}

export const ClickOutsideProvider = ({children}: ClickOutsideProviderProps) => {
  const listenersRef = useRef<HandlerObj[]>([]);
  const currentBatchRef = useRef<HandlerObj[] | null>(null);

  const ctxVal = useMemo(
    () => ({
      register: (handlerObj: HandlerObj) => {
        const listeners = listenersRef.current;
        if (currentBatchRef.current === null) {
          currentBatchRef.current = [];
          // FIXME: this pattern isn't safe.
          // If some underlying useEffect calls useState, all parents and children
          // useEffects are called again before setTimeout is over
          // consider using the subRef pattern of `useGlobalKeyPress`. Not sure it's safe either
          setTimeout(() => {
            listeners.unshift(...currentBatchRef.current!.reverse());
            currentBatchRef.current = null;
          });
        }
        currentBatchRef.current.push(handlerObj);
        return () => {
          if (currentBatchRef.current && currentBatchRef.current.indexOf(handlerObj) > -1) {
            currentBatchRef.current.splice(currentBatchRef.current.indexOf(handlerObj), 1);
          } else if (listeners.indexOf(handlerObj) > -1) {
            listeners.splice(listeners.indexOf(handlerObj), 1);
          } else {
            console.warn("Handler found no where!?");
          }
        };
      },
    }),
    []
  );

  const mouseDownPosRef = useRef<{x: number; y: number} | null>(null);

  const handleClick = (e: React.MouseEvent) => {
    if (mouseDownPosRef.current) {
      const {x, y} = mouseDownPosRef.current;
      mouseDownPosRef.current = null;
      const dx = Math.abs(e.clientX - x);
      const dy = Math.abs(e.clientY - y);
      // sloppy click, ignore
      if (dx > 5 || dy > 5) return;
    }

    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      if (!(range.startContainer === range.endContainer && range.startOffset === range.endOffset))
        return;
    }
    const clickOnInteractive = isEventWithinInteractiveNode(e);

    // there's already another onClick handler. So we don't want to deselect nothing. Maybe.
    listenersRef.current.some(({handler, strict, escOnly}) => {
      if ((!clickOnInteractive || strict) && !escOnly) {
        return handler(e) !== false;
      }
      return false;
    });
    e.stopPropagation();
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    mouseDownPosRef.current = {x: e.clientX, y: e.clientY};
  };

  return (
    <ClickOutsideContext.Provider value={ctxVal}>
      {children({onClick: handleClick, onMouseDownCapture: handleMouseDown})}
    </ClickOutsideContext.Provider>
  );
};

interface UseEscOptions {
  isDisabled?: boolean;
}

export const useEsc = (handler: () => void, {isDisabled}: UseEscOptions = {}) => {
  useGlobalKeyPress({
    disabled: isDisabled,
    key: "Escape",
    fn: handler,
    ignoreTarget: true,
  });
};

interface UseClickOutsideOptions {
  isDisabled?: boolean;
  strict?: boolean;
}

/**
 * Custom hook to detect clicks outside of a component.
 *
 * @param onClickOutside - The function to call when a click outside is detected.
 * @param options - Additional options for the hook.
 * @returns An object containing an `onClick` handler that should be added on the element you want to track the clicks.
 */
export const useClickOutside = (
  onClickOutside: (e?: React.MouseEvent | KeyboardEvent, force?: boolean) => void,
  {isDisabled = false, strict = false}: UseClickOutsideOptions = {}
) => {
  const ctx = useContext(ClickOutsideContext);
  const fnRef = useRef<typeof onClickOutside>();
  const justPassedRef = useRef<Event | null>(null);
  useEffect(() => {
    fnRef.current = onClickOutside;
  }, [onClickOutside]);
  useEffect(() => {
    if (!isDisabled) {
      const handleClickOutside = (e: React.MouseEvent | KeyboardEvent, force?: boolean) => {
        if (!fnRef.current) return false;
        if (force) return fnRef.current();
        const justPassed = justPassedRef.current;
        justPassedRef.current = null;
        if ("nativeEvent" in e && e.nativeEvent === justPassed) return false;
        return fnRef.current();
      };
      return ctx.register({handler: handleClickOutside, strict});
    }
  }, [ctx, isDisabled, strict]);

  useGlobalKeyPress({
    key: "Escape",
    fn: (e) => (isDisabled ? false : onClickOutside(e, true)),
    ignoreTarget: true,
  });

  const onClick = useCallback((e: React.MouseEvent) => {
    justPassedRef.current = e.nativeEvent;
  }, []);
  return {onClick};
};

interface ClickOutsideProps {
  children: (props: {onClick: (e: React.MouseEvent) => void}) => ReactNode;
  onClickOutside: (e?: React.MouseEvent | KeyboardEvent, force?: boolean) => void;
}

export const ClickOutside = ({children, onClickOutside}: ClickOutsideProps) =>
  children(useClickOutside(onClickOutside));
